import { useCallback, useEffect, useRef, useState } from 'react';

import { hasLivePendingToken, pendingPollInterval } from '@/lib/token-progress';

/** How long a freshly-issued row keeps its ticked step list before collapsing. */
const RESOLVED_HOLD_MS = 4_000;

interface PendingRow {
  token?: string | null;
  createdAt: string;
  /**
   * Needed here, not just for display: a row carrying a provider message but
   * no token has been refused, and the poll and the clock must both let it go.
   */
  description?: string | null;
}

interface PollControls {
  startPolling: (intervalMs: number) => void;
  stopPolling: () => void;
  refetch: () => Promise<unknown>;
}

interface PendingTokenPolling {
  /** One clock shared by every row on the tab. */
  now: number;
  checkAgain: () => void;
  isChecking: boolean;
}

function isDocumentVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

/**
 * Keeps a token list refreshing while any row is still waiting on Pay@.
 *
 * Polling is a display concern only — it stops looking, it never cancels
 * anything server-side, and the row stays live whatever we do here. Tokens have
 * arrived hours after we gave up watching; they simply show up on the next
 * load. See docs/adr/0003-token-progress-display-timeout-only.md.
 *
 * The one thing that does end a row's life here is Pay@ answering without a
 * token. `hasLivePendingToken` and `pendingPollInterval` both drop those rows,
 * so a refused request costs neither a timer nor a request.
 */
export function usePendingTokenPolling(
  items: readonly PendingRow[],
  { startPolling, stopPolling, refetch }: PollControls
): PendingTokenPolling {
  const [now, setNow] = useState(() => Date.now());
  const [isChecking, setIsChecking] = useState(false);

  const isLive = hasLivePendingToken(items, now);

  // One-second clock, running only while a row can still change state. It is
  // what carries a row across the first-poll, slow and cutoff boundaries.
  useEffect(() => {
    if (!isLive) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [isLive]);

  // Apollo 3 does not pause polling for a hidden tab, so we do it here.
  const [isVisible, setIsVisible] = useState(isDocumentVisible);
  useEffect(() => {
    if (!isLive) return;
    const onVisibilityChange = () => {
      const visible = isDocumentVisible();
      setIsVisible(visible);
      // Catch up immediately rather than making the operator watch a stale
      // spinner for a whole interval after switching back.
      if (visible) {
        setNow(Date.now());
        void refetch();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isLive, refetch]);

  // startPolling restarts Apollo's timer on every call, so re-issuing the same
  // interval each time the clock ticks would reset it forever and nothing would
  // ever fire. Only act when the desired cadence actually changes.
  const activeIntervalRef = useRef(0);
  useEffect(() => {
    const desired = isVisible ? pendingPollInterval(items, now) : 0;
    if (desired === activeIntervalRef.current) return;
    activeIntervalRef.current = desired;
    if (desired > 0) startPolling(desired);
    else stopPolling();
  }, [items, now, isVisible, startPolling, stopPolling]);

  useEffect(
    () => () => {
      activeIntervalRef.current = 0;
      stopPolling();
    },
    [stopPolling]
  );

  // Deliberately a single look, leaving the row stopped. Resuming the poll here
  // would be an endless manual loop with no way out.
  const checkAgain = useCallback(() => {
    setIsChecking(true);
    void refetch().finally(() => {
      setNow(Date.now());
      setIsChecking(false);
    });
  }, [refetch]);

  return { now, checkAgain, isChecking };
}

interface IdentifiedRow {
  id: string;
  token?: string | null;
}

/**
 * Ids of rows whose token landed while we were watching, held briefly so the
 * completed step list stays on screen before the row collapses to its normal
 * compact form. The row shows the real token throughout — only the decoration
 * lingers.
 */
export function useJustResolvedTokens(items: readonly IdentifiedRow[]): ReadonlySet<string> {
  const [resolved, setResolved] = useState<ReadonlySet<string>>(() => new Set());
  const previousRef = useRef<Map<string, boolean> | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const current = new Map(items.map(item => [item.id, item.token != null]));
    const previous = previousRef.current;
    previousRef.current = current;

    // First pass only records the baseline — rows already issued when the tab
    // opened were not resolved in front of anyone.
    if (!previous) return;

    const landed = items
      .filter(item => item.token != null && previous.get(item.id) === false)
      .map(item => item.id);
    if (landed.length === 0) return;

    setResolved(prev => new Set([...prev, ...landed]));
    // Not cleaned up on re-run: a poll landing mid-hold must not cut the hold
    // short. Unmount clears them via the effect above.
    timersRef.current.push(
      setTimeout(() => {
        setResolved(prev => {
          const next = new Set(prev);
          landed.forEach(id => next.delete(id));
          return next;
        });
      }, RESOLVED_HOLD_MS)
    );
  }, [items]);

  return resolved;
}

interface SettlingRow {
  id: string;
  token?: string | null;
  /** Money that counts, token or not — see isDepositOnly in lib/token-progress. */
  countsTowardBalance?: boolean | null;
}

/**
 * Calls back when money settles on the list while somebody is watching it.
 *
 * The tab polls; the figures around it do not. A payment the deposit swallowed
 * whole settles the deposit server-side the moment the callback lands, so the
 * contract's deposit and balance on the same screen are stale from that
 * instant — showing "Deposit paid" beside a deposit that still reads R500 is
 * worse than showing neither.
 *
 * Fires on a row that gains a token, on a row that starts counting, and on a
 * row that arrives already settled (a till payment landing mid-view). The first
 * pass only records a baseline: what was already settled when the tab opened
 * settled before anyone was looking, and refetching the page for it would be a
 * jump with no cause.
 */
export function useMoneySettled(items: readonly SettlingRow[], onSettled?: () => void): void {
  const previousRef = useRef<Map<string, boolean> | null>(null);
  const callbackRef = useRef(onSettled);
  callbackRef.current = onSettled;

  useEffect(() => {
    const settled = (row: SettlingRow) => row.token != null || row.countsTowardBalance === true;
    const current = new Map(items.map(item => [item.id, settled(item)]));
    const previous = previousRef.current;
    previousRef.current = current;
    if (!previous) return;

    const landed = items.some(item => settled(item) && previous.get(item.id) !== true);
    if (landed) callbackRef.current?.();
  }, [items]);
}
